import { useEffect, useState } from "react";
import type { DocumentListResponse, DocumentResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { label, plain, shortTime } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Icon } from "../ui/Icon.tsx";
import styles from "../chats/TopicPanel.module.css";
import own from "../board/BoardList.module.css";

/** The app's templates (DocumentRepository.kt). */
const TEMPLATES: [string, string, string][] = [
  ["empty", "Leer", ""],
  ["shopping", "Einkaufsliste", "- [ ] "],
  ["todo", "To-dos", "## Offen\n- [ ] \n\n## Erledigt\n"],
  ["trip", "Reise", "## Wann\n\n## Wo\n\n## Wer\n\n## Packliste\n- [ ] \n\n## Budget\n"],
];

/** The documents of a chat next to it, with "Neues Dokument". */
export function DocumentList({ conversationId, onOpen, onClose }: { conversationId: string; onOpen: (id: string) => void; onClose: () => void }) {
  const services = useServices();
  const { documents, conversation } = useStore((s) => ({ documents: [...s.documents.values()].filter((d) => d.conversation_id === conversationId && !d.archived).sort((a, b) => b.updated_at.localeCompare(a.updated_at)), conversation: s.conversations.get(conversationId) }));
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState("empty");
  const [error, setError] = useState<string>();

  useEffect(() => {
    services.session.call<DocumentListResponse>(`/v1/conversations/${conversationId}/documents`).then((r) => {
      for (const d of r.documents) services.store.documents.set(d.id, d);
      services.store.changed();
    }, () => undefined);
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    try {
      const body = TEMPLATES.find((t) => t[0] === template)![2];
      const d = await services.session.call<DocumentResponse>(`/v1/conversations/${conversationId}/documents`, { method: "POST", json: { title: title.trim() || "Dokument", body, client_id: crypto.randomUUID() } });
      services.store.documents.set(d.id, d);
      services.store.changed();
      onOpen(d.id);
    } catch {
      setError("Das Dokument ließ sich nicht anlegen.");
    }
  }

  const who = (id: string) => (id === services.store.me ? "dir" : label(conversation?.members.find((m) => m.user.id === id)?.user).split(" ")[0]);
  return (
    <aside className={styles.panel} aria-label="Dokumente">
      <header className={styles.head}>
        <Icon name="doc" />
        <b>Dokumente</b>
        <button onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
      </header>
      <div className={styles.body}>
        {!creating ? (
          <button className={styles.playAll} onClick={() => setCreating(true)}>
            <Icon name="add" size={18} /> Neues Dokument
          </button>
        ) : (
          <div className={own.form}>
            <input autoFocus placeholder="Titel" value={title} maxLength={80} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void create()} />
            <div className={own.choices}>
              {TEMPLATES.map(([id, name]) => (
                <button key={id} className={template === id ? own.on : undefined} onClick={() => setTemplate(id)}>
                  {name}
                </button>
              ))}
            </div>
            <button className={styles.playAll} onClick={() => void create()}>
              Anlegen
            </button>
            {error && <p className={styles.note}>{error}</p>}
          </div>
        )}
        <div className={styles.label}>IN DIESEM CHAT</div>
        {documents.length === 0 && <p className={styles.note}>Noch kein Dokument.</p>}
        {documents.map((d) => (
          <div key={d.id} className={styles.segment}>
            <button className={styles.text} onClick={() => onOpen(d.id)}>
              <b>
                {d.title}
                {d.suggestion ? " •" : ""}
              </b>
              <span>
                {plain(d.body).slice(0, 60) || "leer"} · geändert von {d.updated_by === "00000000-0000-4000-8000-000000000a6e" ? "Voxie" : who(d.updated_by)} · {shortTime(d.updated_at)}
              </span>
            </button>
            <button className={styles.play} onClick={() => onOpen(d.id)} aria-label={`${d.title} öffnen`}>
              <Icon name="open" size={18} />
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}
